//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ForwardedRef, forwardRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { FormFieldControllerProps } from '@components/form/field-controller/FormFieldController';

interface FormTextInputProps<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
> extends Omit<
      React.ComponentPropsWithoutRef<typeof TextAreaInput>,
      'onChange' | 'errorText' | 'hasError' | 'name' | 'onBlur' | 'value'
    >,
    FormFieldControllerProps<T, TFieldName> {}

export const FormTextAreaInput = forwardRef(
  <T extends FieldValues, TFieldName extends FieldPath<T>>(
    props: FormTextInputProps<T, TFieldName>,
    ref?: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const { onChange, error, invalid, ...rest } = props;
    return (
      <TextAreaInput
        {...rest}
        onChange={onChange}
        errorText={error?.message}
        hasError={invalid}
        ref={ref}
      />
    );
  },
);

FormTextAreaInput.displayName = 'FormTextAreaInput';
